import React, { useState, useEffect } from 'react';
import { Modal, Button, Tooltip, message, Spin } from 'antd';
import {
  DownloadOutlined,
  ExportOutlined,
  FilePdfOutlined,
  ShareAltOutlined,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

const ProjectDocumentPreviewModal = ({ visible, onClose, document, onShare }) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const documentUrl = (document && (document.source_url || document.url || document.document_url)) || '';
  const documentName = (document && (document.name || document.original_filename)) || 'Document Preview';
  const pageCount = document && document.page_count;
  const fileSize = document && document.file_size;

  const [detectedPageCount, setDetectedPageCount] = useState(pageCount || 1);

  // Reset loading states and inspect PDF page count when doc or visibility changes
  useEffect(() => {
    setDetectedPageCount(document?.page_count || 1);
    if (visible && documentUrl) {
      setIframeLoading(true);
      setDownloading(false);

      // Dynamically detect real page count from PDF stream if possible
      fetch(documentUrl)
        .then((res) => {
          if (!res.ok) throw new Error('Fetch failed');
          return res.blob();
        })
        .then((blob) => blob.text())
        .then((text) => {
          // Count /Type /Page (excluding /Type /Pages)
          const matches = text.match(/\/Type\s*\/Page[^s]/gi);
          if (matches && matches.length > 0) {
            setDetectedPageCount(matches.length);
            return;
          }
          // Fallback: match /Count in Pages dictionary
          const countMatch = text.match(/\/Count\s+(\d+)/i);
          if (countMatch && countMatch[1]) {
            const parsed = parseInt(countMatch[1], 10);
            if (parsed > 0) setDetectedPageCount(parsed);
          }
        })
        .catch((err) => {
          console.warn('Could not inspect PDF for page count:', err);
        });
    }
  }, [visible, documentUrl, document?.page_count]);

  if (!document) return null;

  // Open PDF strictly in a new browser tab for viewing/printing
  const handleOpenNewTab = () => {
    if (!documentUrl) return;
    window.open(documentUrl, '_blank', 'noopener,noreferrer');
  };

  // Actually download the PDF file to disk as a blob (bypasses cross-origin browser tab opening)
  const handleDownload = async () => {
    if (!documentUrl) return;
    setDownloading(true);
    try {
      const cleanName = (documentName || 'document').trim();
      const filename = cleanName.toLowerCase().endsWith('.pdf') ? cleanName : `${cleanName}.pdf`;

      const response = await fetch(documentUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Network response not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      message.success('Download started');
    } catch (err) {
      console.warn('Direct blob download failed, falling back to standard download:', err);
      // Fallback
      const fallbackLink = window.document.createElement('a');
      fallbackLink.href = documentUrl;
      fallbackLink.target = '_blank';
      fallbackLink.download = documentName;
      window.document.body.appendChild(fallbackLink);
      fallbackLink.click();
      window.document.body.removeChild(fallbackLink);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      wrapClassName="project-document-preview-modal-wrap"
      className="project-document-preview-modal"
      title={
        <div className="preview-modal-title-wrapper">
          {/* Left: Icon, Name & Metadata Badges */}
          <div className="preview-modal-title-left">
            <div className="preview-pdf-badge-icon">
              <FilePdfOutlined />
            </div>

            <div className="preview-modal-text-content">
              <Tooltip title={documentName} placement="topLeft">
                <div className="preview-doc-title-text">
                  {documentName}
                </div>
              </Tooltip>

              <div className="preview-doc-meta-pills">
                <span className="pill-badge pill-pdf">PDF</span>
                {detectedPageCount ? (
                  <span className="pill-badge pill-info">
                    {detectedPageCount} {detectedPageCount === 1 ? 'Page' : 'Pages'}
                  </span>
                ) : null}
                {fileSize ? (
                  <span className="pill-badge pill-info">
                    {(fileSize / 1024).toFixed(0)} KB
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      }
      visible={visible}
      open={visible}
      onCancel={onClose}
      destroyOnClose={true}
      width="94%"
      style={{
        top: '2vh',
        maxWidth: 1280,
        paddingBottom: 0,
        margin: '0 auto',
      }}
      bodyStyle={{
        padding: 0,
        background: '#0f172a',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
      footer={
        <div className="preview-modal-footer-container">
          {/* Footer Left: File Identity Badge (Desktop) */}
          <div className="preview-modal-footer-meta">
            <span className="preview-doc-meta-chip">
              <FilePdfOutlined style={{ color: '#dc2626' }} />
              <span className="preview-doc-meta-name">{documentName}</span>
            </span>
            <span className="preview-doc-meta-sub">Floor Detective Viewer</span>
          </div>

          {/* Footer Right: Action Buttons with Icon + Label */}
          <div className="preview-modal-footer-actions">
            {documentUrl && (
              <>
                <Tooltip title="Open in New Tab" placement="top">
                  <Button
                    className="preview-btn-action preview-btn-open"
                    icon={<ExportOutlined />}
                    onClick={handleOpenNewTab}
                    aria-label="Open in New Tab"
                  >
                    <span className="preview-btn-label">Open in Tab</span>
                  </Button>
                </Tooltip>

                {onShare && (
                  <Tooltip title="Share Document" placement="top">
                    <Button
                      className="preview-btn-action preview-btn-share"
                      icon={<ShareAltOutlined />}
                      onClick={onShare}
                      aria-label="Share Document"
                    >
                      <span className="preview-btn-label">Share</span>
                    </Button>
                  </Tooltip>
                )}

                <Button
                  className="preview-btn-action preview-btn-download"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  loading={downloading}
                >
                  <span className="preview-btn-label">Download PDF</span>
                </Button>
              </>
            )}
            <Tooltip title="Close Preview" placement="top">
              <Button
                className="preview-btn-action preview-btn-close"
                icon={<CloseOutlined />}
                onClick={onClose}
                aria-label="Close Preview"
              >
                <span className="preview-btn-label">Close</span>
              </Button>
            </Tooltip>
          </div>
        </div>
      }
    >
      {documentUrl ? (
        <div style={{ position: 'relative', width: '100%', height: '100%', flex: 1, minHeight: 0 }}>
          {iframeLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#0f172a',
                color: '#ffffff',
                zIndex: 10,
                gap: 12,
              }}
            >
              <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#38bdf8' }} spin />} />
              <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>
                Loading document preview...
              </span>
            </div>
          )}
          <iframe
            src={`${documentUrl}#toolbar=1&view=FitH`}
            title={documentName}
            width="100%"
            height="100%"
            onLoad={() => setIframeLoading(false)}
            style={{
              border: 'none',
              width: '100%',
              height: '100%',
              display: 'block',
              background: '#ffffff',
            }}
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 350,
            color: '#94a3b8',
            gap: 12,
          }}
        >
          <FilePdfOutlined style={{ fontSize: 48, color: '#64748b' }} />
          <div style={{ fontSize: 14 }}>Unable to load document preview</div>
        </div>
      )}
    </Modal>
  );
};

export default ProjectDocumentPreviewModal;
